import * as React from 'react';
import { CSSTransitionGroup } from 'react-transition-group';
import { VideoPlayer } from '../VideoPlayer';
import { VideoDetailsBar } from '../VideoDetailsBar';
import { VideoMiniList } from '../VideoMiniList';
import { CoachingInformation } from './CoachingInformation';
import { Advert as AdvertComponent } from '../../Advert';
import { AsyncLoader } from '../../Common/AsyncLoader';
import { FixedAspect } from '../../Common/FixedAspect';
import { LoadFailure } from '../../Common/LoadFailure';
import { LoadingSpinner } from '../../Common/LoadingSpinner';
import { SiteMeta } from '../../Utils/SiteMeta';
import { Advert, Video, User } from '../../../models';
import './style.css';

export interface VideoShowProps {
  advertSlotOne?: Array<Advert>;
  advertSlotTwo?: Array<Advert>;
  advertSlotMobileOne?: Array<Advert>;
  advertSlotMobileTwo?: Array<Advert>;
  currentUser?: User;
  getVideoUrl: (video: Video) => string;
  loggedIn: boolean;
  nextVideo?: Video | null;
  pageUrl: string;
  previousVideo?: Video | null;
  savingFavourite: boolean;
  video?: Video;
  videoError: boolean;
  videoLoading: boolean;
  onFavourite: () => void;
  onFirstPlay: () => void;
  onPremiumPlaybackBlocked: () => void;
}

export interface VideoShowState {
  playerReady: boolean;
  played: boolean;
}

export class VideoShow extends React.Component<VideoShowProps, VideoShowState> {
  constructor(props: VideoShowProps) {
    super(props);

    this.state = {
      playerReady: false,
      played: false
    };
  }

  UNSAFE_componentWillReceiveProps(newProps: VideoShowProps) {
    const { video } = this.props;
    const { video: newVideo } = newProps;
    const videoId = video ? video.id : -1;
    const newVideoId = newVideo ? newVideo.id : -1;

    // Clear playerReady + played when switching video
    if (videoId !== newVideoId) {
      this.setState({ playerReady: false, played: false });
    }
  }

  // TODO: Refactor me please!
  render() {
    const {
      advertSlotOne,
      advertSlotTwo,
      advertSlotMobileOne,
      advertSlotMobileTwo,
      currentUser,
      getVideoUrl,
      loggedIn,
      nextVideo,
      onFavourite,
      onPremiumPlaybackBlocked,
      pageUrl,
      previousVideo,
      savingFavourite,
      video,
      videoError,
      videoLoading
    } = this.props;
    const { playerReady } = this.state;

    return (
      <main className="video-show" role="main">
        {video ? (
          <SiteMeta
            title={`Edge of Play | ${video.name}`}
            description={video.description}
            image={video.poster}
            url={getVideoUrl(video)}
          />
        ) : null}
        <div className="video-player-container">
          <div className="video-and-adverts-wrapper">
            <CSSTransitionGroup
              className="video-wrapper"
              transitionName="video-change"
              transitionEnterTimeout={500}
              transitionLeaveTimeout={500}
            >
              <div key={video ? video.id : -1}>
                <AsyncLoader
                  className="video-show-loader edge-of-play-video-container"
                  loaderFactory={(): JSX.Element => (
                    <FixedAspect>
                      <LoadingSpinner />
                    </FixedAspect>
                  )}
                  error={videoError}
                  errorMessageFactory={(errorClasses: string): JSX.Element => (
                    <FixedAspect className="video-show-error-fixed-aspect">
                      <LoadFailure
                        message="Sorry, an error occurred while trying to load the video."
                        className={errorClasses}
                        pageUrl={pageUrl}
                      />
                    </FixedAspect>
                  )}
                  loading={videoLoading}
                  pageUrl={pageUrl}
                >
                  <VideoPlayer
                    currentUser={currentUser}
                    video={video as Video}
                    onPremiumPlaybackBlocked={onPremiumPlaybackBlocked}
                    onReady={this.onPlayerReady}
                    onPlay={this.onVideoPlay}
                  />
                  {playerReady ? null : (
                    <FixedAspect>
                      <LoadingSpinner />
                    </FixedAspect>
                  )}
                </AsyncLoader>
              </div>
            </CSSTransitionGroup>
            <div className="video-page-adverts-wrapper">
              <AdvertComponent
                className="video-page-advert"
                adverts={advertSlotOne}
              />
              <AdvertComponent
                className="video-page-advert"
                adverts={advertSlotTwo}
              />
            </div>
          </div>
          <CSSTransitionGroup
            className="video-details-wrapper"
            transitionName="video-change"
            transitionEnterTimeout={500}
            transitionLeaveTimeout={500}
          >
            <div key={video ? video.id : -1}>
              <AsyncLoader
                className="video-show-details-loader video-details-bar"
                dontShowError={true}
                dontShowLoader={true}
                error={videoError}
                loading={videoLoading}
                pageUrl={pageUrl}
              >
                <VideoDetailsBar
                  getVideoUrl={getVideoUrl}
                  loggedIn={loggedIn}
                  nextVideo={nextVideo}
                  onFavourite={onFavourite}
                  previousVideo={previousVideo}
                  savingFavourite={savingFavourite}
                  video={video as Video}
                />
              </AsyncLoader>
            </div>
          </CSSTransitionGroup>
          <div className="video-page-mobile-adverts-wrapper">
            <AdvertComponent
              className="video-page-advert"
              adverts={advertSlotMobileOne}
            />
            <AdvertComponent
              className="video-page-advert"
              adverts={advertSlotMobileTwo}
            />
          </div>
        </div>
        <AsyncLoader
          className="video-show-coaching-info-loader coaching-information"
          dontShowError={true}
          dontShowLoader={true}
          error={videoError}
          loading={videoLoading}
          pageUrl={pageUrl}
        >
          {this.hasCoachingInformationSection(video) ? (
            <CoachingInformation video={video as Video} />
          ) : null}
          {this.hasCoachingInformationSection(video) ? (
            <div className="breakout-to-full-width divider-bar" />
          ) : null}
        </AsyncLoader>
        {this.hasRelatedVideosSection(video) ? (
          <VideoMiniList
            className="video-show-related-videos-list"
            error={!!(video && video.relatedVideosFetchError)}
            getVideoUrl={Video.getRoute}
            loading={!!(video && video.relatedVideosLoading)}
            pageUrl={pageUrl}
            videos={video && video.relatedVideos ? video.relatedVideos : []}
            title="Related Videos"
          />
        ) : null}
        {this.hasRelatedVideosSection(video) ? (
          /* TODO: Remove this til we have signup below? */
          <div className="breakout-to-full-width divider-bar divider-bar-reversed" />
        ) : null}
        {this.props.children}
      </main>
    );
  }

  private hasCoachingInformationSection = (video?: Video): boolean => {
    return (
      !!video &&
      !!(
        video.coachingInformation ||
        (video.coachingTips && video.coachingTips.length)
      )
    );
  };

  private hasRelatedVideosSection = (video?: Video): boolean => {
    return !!video && !!video.relatedVideos;
  };

  private onPlayerReady = () => {
    this.setState({ playerReady: true });
  };

  private onVideoPlay = () => {
    if (this.state.played) {
      return;
    }

    this.setState({ played: true });

    const { onFirstPlay } = this.props;
    onFirstPlay();
  };
}

export default VideoShow;



// WEBPACK FOOTER //
// ./src/app/components/Video/VideoShow/index.tsx